import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MutableRefObject,
} from 'react';
import {
  SimulationContext,
  type SubSimulation,
  type SubSimulationParams,
  type TargetViewParams,
} from 'contexts/SimulationContext';
import { GeodataContext, type GeodataTarget } from 'contexts/GeodataContext';
import { ValidationContext } from 'contexts/ValidationContext';
import { Grid, TextInput, ViewContext } from 'components/lib';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ButtonGroup from '@mui/material/ButtonGroup';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Tooltip, useMap, useMapEvents, Marker } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import CreateBorderButton from 'views/add-grid/cadastral/CreateBorderButton';
import {
  lv95ToWgs84ForOneCoordinate,
  roundCoordinatesTo8Digits,
  wgs84ToLv95ForOneCoordinate,
} from 'views/add-grid/map/coordinateTransforms';
import { isPointInsideBounds } from 'utils/geometryHelper';
import { DeleteIcon } from 'components/icons';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';
import Copyright from 'components/map/Copyright';
import MousePositionPopup from 'components/map/MousePositionPopup';
import RenderRectangle from 'views/add-geodata/RenderRectangle';
import { parseCoordinateSystemPreference, type CoordinateSystemPreference } from 'utils/coordinateSystemManager';
import {
  DEFAULT_TARGET_VIEW_RAYS_NUMBER,
  DEFAULT_TARGET_VIEW_RADIUS_MAX,
  FOREST_MIN_AREA_DEFAULT,
  FOREST_MIN_AREA_MAX,
  FOREST_MIN_AREA_MIN,
  MAX_TARGET_VIEW_RAYS_NUMBER,
  MIN_TARGET_VIEW_RAYS_NUMBER,
  TARGET_VIEW_RADIUS_MIN,
} from 'constants/simulation';
import { getInputNumberRangeErrorMessage, isInputNumberInRange } from 'utils/inputNumberErrorMessage';
import L from 'leaflet';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const zoomValue = 17;
const targetViewNameInputWidth = '240px';
const targetViewSettingsStyle = {
  marginTop: '0.35rem',
  minWidth: 0,
};
const standardTargetDividerStyle = { my: 1.5 };
const formatTerrainLength = (meters: number) =>
  meters >= 1000 ? `${Number((meters / 1000).toFixed(2))} km` : `${meters} m`;
interface TargetViewGeodata {
  params?: {
    targets?: unknown;
    [key: string]: unknown;
  };
}

interface TargetsGridParams {
  coordinateReferenceSystem?: {
    rotation?: number | string | null;
    point?: {
      x?: number | string | null;
      y?: number | string | null;
      z?: number | string | null;
    } | null;
  } | null;
  manualCenter?: { x?: number | string | null; y?: number | string | null } | null;
}

interface TargetViewProps {
  geodata: TargetViewGeodata | null;
  grid?: { params?: TargetsGridParams | null } | null;
  readOnly?: boolean;
}

interface Lv95Coordinate {
  x: number;
  y: number;
}

interface CustomPolygonData {
  id: number;
  targetId: number;
  name: string;
  polygon: Lv95Coordinate[];
  isSessionCustomPolygon?: boolean;
}

type LocalCustomGeodataTarget = GeodataTarget & { isSessionCustomPolygon?: boolean };

type DrawLayer = L.Polygon & {
  _latlngs: L.LatLng[][];
};

interface DrawCreatedEvent {
  layer: DrawLayer;
}

interface DrawVertexEvent {
  layers?: L.LayerGroup;
}

type VertexLayer = L.Layer & { _latlng?: L.LatLng };
type MapBounds = [[number, number], [number, number]];

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const toInputValue = (value: unknown): number | string | null => {
  if (value == null) return null;
  if (typeof value === 'number' || typeof value === 'string') return value;
  return null;
};

const toNumericTargetIds = (value: unknown): number[] =>
  Array.isArray(value) ? value.map((item) => Number(item)).filter((item): item is number => Number.isFinite(item)) : [];

const getNextTargetId = (targets: GeodataTarget[] = []) => {
  const maxId = targets.reduce((currentMax, target) => {
    const targetId = Number(target?.id);
    return Number.isFinite(targetId) ? Math.max(currentMax, targetId) : currentMax;
  }, 0);
  return maxId + 1;
};

const getTargetType = (subSimulation: SubSimulation): string | null => {
  const targetTypes = [
    ...new Set(
      (Array.isArray(subSimulation?.params?.targets) ? subSimulation.params.targets : [])
        .map((target) => (target as GeodataTarget)?.type)
        .filter(Boolean)
    ),
  ];
  return targetTypes.length === 1 ? String(targetTypes[0]) : null;
};

const getTargetLabel = (targetType: string | null) => {
  switch (targetType) {
    case 'LAKE':
      return 'Lakes';
    case 'RIVER':
      return 'Rivers';
    case 'FOREST':
      return 'Forests';
    case 'CUSTOM':
      return 'Custom Polygons';
    default:
      return 'Target View';
  }
};

const isLv95Coordinate = (value: unknown): value is Lv95Coordinate =>
  Boolean(value) &&
  typeof value === 'object' &&
  Number.isFinite(Number((value as { x?: unknown }).x)) &&
  Number.isFinite(Number((value as { y?: unknown }).y));

const toCustomPolygonDataList = (target: GeodataTarget, fallbackIndex: number): CustomPolygonData[] => {
  const targetId = Number(target?.id);
  if (!Number.isFinite(targetId)) {
    return [];
  }

  const rawPolygons = Array.isArray(target?.polygon) ? (target.polygon as unknown[]) : [];
  const polygons: Lv95Coordinate[][] = [];

  if (rawPolygons.every(isLv95Coordinate)) {
    polygons.push(rawPolygons as Lv95Coordinate[]);
  } else if (rawPolygons.every((entry) => Array.isArray(entry) && entry.every(isLv95Coordinate))) {
    polygons.push(...(rawPolygons as Lv95Coordinate[][]));
  } else {
    rawPolygons.forEach((entry) => {
      if (!Array.isArray(entry)) {
        return;
      }

      if (entry.every(isLv95Coordinate)) {
        polygons.push(entry as Lv95Coordinate[]);
        return;
      }

      const firstRing = entry[0];
      if (Array.isArray(firstRing) && firstRing.every(isLv95Coordinate)) {
        polygons.push(firstRing as Lv95Coordinate[]);
      }
    });
  }

  return polygons
    .map((polygon, index) => {
      return {
        id: targetId * 1000 + index,
        targetId,
        name:
          polygons.length > 1
            ? `${target?.name || `Polygon ${fallbackIndex}`} ${index + 1}`
            : target?.name || `Polygon ${fallbackIndex}`,
        polygon,
        isSessionCustomPolygon: Boolean((target as LocalCustomGeodataTarget)?.isSessionCustomPolygon),
      };
    })
    .filter((polygon) => polygon.polygon.length > 0);
};

const DrawVertexBoundaryGuard = ({ bounds, onOutside }: { bounds: MapBounds; onOutside: () => void }) => {
  const drawVertexHandler = (event: DrawVertexEvent) => {
    let latestVertex: L.LatLng | null = null;

    event?.layers?.eachLayer((layer: L.Layer) => {
      const vertexLayer = layer as VertexLayer;
      if (vertexLayer?._latlng) {
        latestVertex = vertexLayer._latlng;
      }
    });

    if (latestVertex && !isPointInsideBounds(latestVertex, bounds)) {
      onOutside();
    }
  };

  useMapEvents({ 'draw:drawvertex': drawVertexHandler } as unknown as L.LeafletEventHandlerFnMap);
  return null;
};

interface TargetsMapSectionProps {
  mapId: number;
  mapCenter: [number, number];
  mapZoom: number;
  bounds: MapBounds;
  boundaryLabel: { title: string; details: string };
  customPolygons: CustomPolygonData[];
  siteCenterLocation: [number, number] | null;
  coordinateSystem: CoordinateSystemPreference;
  onCreated: (event: DrawCreatedEvent) => void;
  onDrawStart: () => void;
  onDrawStop: () => void;
  onOutsideFarTerrainVertex: () => void;
  readOnly: boolean;
  mapRefs: MutableRefObject<Record<number, L.Map>>;
}

const MapRefSync = ({ mapId, mapRefs }: Pick<TargetsMapSectionProps, 'mapId' | 'mapRefs'>) => {
  const map = useMap();

  useEffect(() => {
    mapRefs.current[mapId] = map;
    return () => {
      if (mapRefs.current[mapId] === map) delete mapRefs.current[mapId];
    };
  }, [map, mapId, mapRefs]);

  return null;
};

const TargetsMapSection = memo(
  ({
    mapId,
    mapCenter,
    mapZoom,
    bounds,
    boundaryLabel,
    customPolygons,
    siteCenterLocation,
    coordinateSystem,
    onCreated,
    onDrawStart,
    onDrawStop,
    onOutsideFarTerrainVertex,
    readOnly,
    mapRefs,
  }: TargetsMapSectionProps) => {
    const rectangleRef = useRef<L.Rectangle | null>(null);

    return (
      <Grid>
        <div data-custom-map-id={mapId}>
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom={true}
            style={{ height: 560, resize: 'both' }}
          >
            <MapRefSync mapId={mapId} mapRefs={mapRefs} />
            <MousePositionPopup
              key={[
                coordinateSystem?.mode,
                coordinateSystem?.centerX,
                coordinateSystem?.centerY,
                coordinateSystem?.centerZ,
                coordinateSystem?.alpha,
              ].join(':')}
              coordinateSystem={coordinateSystem}
            />
            <Copyright />
            <DrawVertexBoundaryGuard bounds={bounds} onOutside={onOutsideFarTerrainVertex} />
            <RenderRectangle
              bounds={bounds}
              style={{ color: 'blue', weight: 2 }}
              rectangleRef={rectangleRef}
              label={boundaryLabel}
            />
            {siteCenterLocation && <Marker position={siteCenterLocation} />}

            <FeatureGroup>
              {customPolygons.map((polygon, index) => (
                <Polygon
                  key={polygon?.id ?? `${polygon.name}-${index}`}
                  positions={(polygon.polygon || []).map((coordinate) => {
                    const { lat, lng } = lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y);
                    return [lat, lng];
                  })}
                  pathOptions={{ color: 'red' }}
                >
                  {!readOnly && <Tooltip direction="top">{`Polygon ${index + 1}`}</Tooltip>}
                </Polygon>
              ))}
              <EditControl
                position="topright"
                onCreated={onCreated}
                onDrawStart={onDrawStart}
                onDrawStop={onDrawStop}
                draw={{
                  polygon: !readOnly,
                  circle: false,
                  rectangle: false,
                  polyline: false,
                  marker: false,
                  circlemarker: false,
                }}
                edit={{
                  edit: false,
                  remove: false,
                }}
              />
            </FeatureGroup>

            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://tile.tracestrack.com/topo_en/{z}/{x}/{y}.png?key=4fbec46b758f2c5c4abb7ece6e66d5ef"
            />
          </MapContainer>
        </div>
      </Grid>
    );
  },
  (prev, next) => {
    if (prev.readOnly !== next.readOnly) return false;
    if (prev.mapId !== next.mapId) return false;
    if (prev.mapZoom !== next.mapZoom) return false;
    if (prev.boundaryLabel.title !== next.boundaryLabel.title) return false;
    if (prev.boundaryLabel.details !== next.boundaryLabel.details) return false;
    if (prev.customPolygons !== next.customPolygons) return false;
    if (prev.bounds !== next.bounds) return false;
    if (prev.coordinateSystem?.mode !== next.coordinateSystem?.mode) return false;
    if (prev.coordinateSystem?.centerX !== next.coordinateSystem?.centerX) return false;
    if (prev.coordinateSystem?.centerY !== next.coordinateSystem?.centerY) return false;
    if (prev.coordinateSystem?.centerZ !== next.coordinateSystem?.centerZ) return false;
    if (prev.coordinateSystem?.alpha !== next.coordinateSystem?.alpha) return false;
    if (prev.mapCenter?.[0] !== next.mapCenter?.[0]) return false;
    if (prev.mapCenter?.[1] !== next.mapCenter?.[1]) return false;
    if (prev.siteCenterLocation?.[0] !== next.siteCenterLocation?.[0]) return false;
    if (prev.siteCenterLocation?.[1] !== next.siteCenterLocation?.[1]) return false;
    return true;
  }
);

const TargetView = ({ geodata, grid, readOnly = false }: TargetViewProps) => {
  const context = useContext(ViewContext);
  const { setSimulation, subSimulations, setSubSimulationParams, setSubSimulationName } = useContext(SimulationContext);
  const { geodataParams, setGeodataParams } = useContext(GeodataContext);
  const {
    isGeodataCustomPolygonValid,
    setIsGeodataCustomPolygonValid,
    geodataCustomPolygonErrorMessage,
    setGeodataCustomPolygonErrorMessage,
    clearValidationForGeodataCustomPolygon,
  } = useContext(ValidationContext);

  const hasUserToggledCustomViewRef = useRef(false);
  const hasInitializedCustomViewFromDataRef = useRef(false);
  const hasOutsideVertexBySimulationRef = useRef<Record<number, boolean>>({});
  const isCancellingDrawBySimulationRef = useRef<Record<number, boolean>>({});
  const customMapRefs = useRef<Record<number, L.Map>>({});

  const coordinateSystemPreference = useMemo(() => parseCoordinateSystemPreference(grid?.params ?? undefined), [grid]);
  const effectiveTargets = useMemo<GeodataTarget[]>(
    () =>
      Array.isArray(geodataParams?.targets) && geodataParams.targets.length > 0
        ? geodataParams.targets
        : Array.isArray(geodata?.params?.targets)
          ? (geodata.params.targets as GeodataTarget[])
          : [],
    [geodata?.params?.targets, geodataParams?.targets]
  );

  const targetsById = useMemo(() => {
    const map = new Map<number, GeodataTarget>();
    effectiveTargets.forEach((target) => {
      const targetId = Number(target?.id);
      if (Number.isFinite(targetId)) {
        map.set(targetId, target);
      }
    });
    return map;
  }, [effectiveTargets]);

  const [isCustomChecked, setIsCustomChecked] = useState(false);

  const resolvedCenterLocation = useMemo<[number, number] | null>(() => {
    const manualCenterX = Number(grid?.params?.manualCenter?.x);
    const manualCenterY = Number(grid?.params?.manualCenter?.y);
    if (Number.isFinite(manualCenterX) && Number.isFinite(manualCenterY)) {
      const { lat, lng } = lv95ToWgs84ForOneCoordinate(manualCenterX, manualCenterY);
      return [lat, lng];
    }
    return null;
  }, [grid?.params?.manualCenter]);

  const mapCenter = resolvedCenterLocation;
  const targetViewRadiusMax = useMemo(() => {
    const intermediateLength = Number(geodataParams?.lengthMid ?? 0);
    const farLength = Number(geodataParams?.lengthFar ?? 0);
    const radiusMax = intermediateLength + farLength;
    return Number.isFinite(radiusMax) && radiusMax > 0 ? radiusMax : DEFAULT_TARGET_VIEW_RADIUS_MAX;
  }, [geodataParams?.lengthFar, geodataParams?.lengthMid]);
  const targetViewLimitLabel = useMemo(() => {
    const intermediateLength = Number(geodataParams?.lengthMid ?? 0) || 0;
    const farLength = Number(geodataParams?.lengthFar ?? 0) || 0;
    return {
      title: `Target drawing limit: ${formatTerrainLength(targetViewRadiusMax)} from site center`,
      details: `${formatTerrainLength(intermediateLength)} Intermediate Terrain + ${formatTerrainLength(farLength)} Far Terrain`,
    };
  }, [geodataParams?.lengthFar, geodataParams?.lengthMid, targetViewRadiusMax]);

  const bounds = useMemo<MapBounds | null>(() => {
    if (!mapCenter) return null;
    if (!Number.isFinite(targetViewRadiusMax) || targetViewRadiusMax <= 0) return null;
    const centerLv95 = wgs84ToLv95ForOneCoordinate(mapCenter[1], mapCenter[0]);
    const southWest = lv95ToWgs84ForOneCoordinate(
      centerLv95.x - targetViewRadiusMax,
      centerLv95.y - targetViewRadiusMax
    );
    const northEast = lv95ToWgs84ForOneCoordinate(
      centerLv95.x + targetViewRadiusMax,
      centerLv95.y + targetViewRadiusMax
    );
    return [
      [southWest.lat, southWest.lng],
      [northEast.lat, northEast.lng],
    ];
  }, [mapCenter, targetViewRadiusMax]);

  const resolveTargetsForSubSimulation = useCallback(
    (subSimulation: SubSimulation): GeodataTarget[] => {
      const paramTargets = Array.isArray(subSimulation?.params?.targets)
        ? (subSimulation.params.targets as GeodataTarget[])
        : [];
      const targetIds = toNumericTargetIds(subSimulation?.params?.targetIds);
      const resolvedTargets = targetIds.map((targetId) => targetsById.get(targetId)).filter(Boolean) as GeodataTarget[];
      const mergedTargets = [...resolvedTargets, ...paramTargets];
      const seenIds = new Set<number>();

      return mergedTargets.filter((target) => {
        const targetId = Number(target?.id);
        if (!Number.isFinite(targetId)) {
          return Boolean(target?.type);
        }
        if (seenIds.has(targetId)) {
          return false;
        }
        seenIds.add(targetId);
        return true;
      });
    },
    [targetsById]
  );

  const isCustomTargetSubSimulation = useCallback(
    (subSimulation: SubSimulation) => {
      if (subSimulation.simType !== 'TARGETVIEW') {
        return false;
      }

      if (Boolean(subSimulation.params?.customPolygonView)) {
        return true;
      }

      return resolveTargetsForSubSimulation(subSimulation).some((target) => target?.type === 'CUSTOM');
    },
    [resolveTargetsForSubSimulation]
  );

  const standardSubSimulations = subSimulations.filter((subSimulation) => {
    if (subSimulation.simType !== 'TARGETVIEW') {
      return false;
    }

    return !isCustomTargetSubSimulation(subSimulation);
  });

  const customSubSimulations = useMemo(
    () => subSimulations.filter((subSimulation) => isCustomTargetSubSimulation(subSimulation)),
    [isCustomTargetSubSimulation, subSimulations]
  );

  const buildCustomTargetParams = useCallback(
    (params: SubSimulationParams = {}, targetIds: number[] = []): TargetViewParams => ({
      ...params,
      customPolygonView: true,
      enabled: (params?.enabled as boolean) ?? true,
      desiredCellCount: Number.isFinite(Number(params?.desiredCellCount))
        ? Number(params.desiredCellCount)
        : DEFAULT_TARGET_VIEW_RAYS_NUMBER,
      radius: Number.isFinite(Number(params?.radius)) ? Number(params.radius) : 0,
      targetIds,
      targets: targetIds.map((targetId) => targetsById.get(targetId)).filter(Boolean),
    }),
    [targetsById]
  );

  const replaceTargets = useCallback(
    (nextTargets: GeodataTarget[]) => {
      setGeodataParams({ targets: nextTargets });
    },
    [setGeodataParams]
  );

  const removeCustomTargetsByIds = useCallback(
    (targetIdsToRemove: number[]) => {
      if (targetIdsToRemove.length === 0) {
        return;
      }

      const idsSet = new Set(targetIdsToRemove);
      replaceTargets(
        effectiveTargets.filter((target) => {
          const targetId = Number(target?.id);
          return !Number.isFinite(targetId) || !idsSet.has(targetId);
        })
      );
    },
    [effectiveTargets, replaceTargets]
  );

  const getNextSubSimulationId = (subs: SubSimulation[] = []) => {
    if (!subs.length) {
      return 0;
    }
    return Math.max(...subs.map((subSimulation) => Number(subSimulation.id) || 0)) + 1;
  };

  const createCustomSubSimulation = useCallback(
    (id: number, index: number): SubSimulation => ({
      id,
      name: `Custom Simulation ${index}`,
      simType: 'TARGETVIEW',
      type: 'subsimulation',
      params: {
        ...buildCustomTargetParams({}, []),
        customPolygonView: true,
        enabled: true,
        desiredCellCount: DEFAULT_TARGET_VIEW_RAYS_NUMBER,
        radius: 0,
      },
    }),
    [buildCustomTargetParams]
  );

  const getCustomPolygonsForSubSimulation = useCallback(
    (subSimulation: SubSimulation) => {
      const polygonsFromTargets = resolveTargetsForSubSimulation(subSimulation)
        .filter((target) => target?.type === 'CUSTOM')
        .flatMap((target, index) => toCustomPolygonDataList(target, index + 1));

      if (polygonsFromTargets.length > 0) {
        return polygonsFromTargets;
      }

      if (subSimulation?.simType !== 'TARGETVIEW' || subSimulation?.params?.type !== 'CUSTOM') {
        return [];
      }

      return toCustomPolygonDataList(
        {
          id: Number(subSimulation.id),
          type: 'CUSTOM',
          name: subSimulation?.params?.name as string | undefined,
          polygon: subSimulation?.params?.polygon as GeodataTarget['polygon'],
        } as GeodataTarget,
        1
      );
    },
    [resolveTargetsForSubSimulation]
  );

  const hasPersistedCustomContent =
    customSubSimulations.length > 0 || effectiveTargets.some((target) => target?.type === 'CUSTOM');

  useEffect(() => {
    if (hasUserToggledCustomViewRef.current || hasInitializedCustomViewFromDataRef.current) {
      return;
    }

    setIsCustomChecked(hasPersistedCustomContent);
    hasInitializedCustomViewFromDataRef.current = true;
  }, [hasPersistedCustomContent]);

  useEffect(() => {
    if (!hasInitializedCustomViewFromDataRef.current) {
      return;
    }

    setSimulation((prevSimulation) => {
      let changed = false;

      const nextSubSimulations = (prevSimulation?.subSimulations || []).map((subSimulation) => {
        if (!isCustomTargetSubSimulation(subSimulation)) {
          return subSimulation;
        }

        const targetIds = toNumericTargetIds(subSimulation?.params?.targetIds).filter((targetId) =>
          targetsById.has(targetId)
        );
        const nextParams = buildCustomTargetParams(subSimulation.params, targetIds);
        const currentTargets = Array.isArray(subSimulation.params?.targets) ? subSimulation.params.targets : [];
        const currentTargetIds = toNumericTargetIds(subSimulation.params?.targetIds);
        const shouldUpdate =
          !Boolean(subSimulation.params?.customPolygonView) ||
          currentTargetIds.length !== nextParams.targetIds.length ||
          currentTargetIds.some((targetId, index) => targetId !== nextParams.targetIds[index]) ||
          currentTargets.length !== nextParams.targets.length;

        if (!shouldUpdate) {
          return subSimulation;
        }

        changed = true;
        return {
          ...subSimulation,
          params: nextParams,
        };
      });

      if (!changed) {
        return prevSimulation;
      }

      return {
        ...prevSimulation,
        subSimulations: nextSubSimulations,
      };
    });
  }, [buildCustomTargetParams, isCustomTargetSubSimulation, setSimulation, targetsById]);

  const handleEnabledChange = (subSimulationId: number, enabled: boolean) => {
    setSubSimulationParams(subSimulationId, { enabled });
  };

  const handleNameChange = (subSimulationId: number, name: string) => {
    setSubSimulationName(name, subSimulationId);
  };

  const handleDesiredCellCountChange = (subSimulationId: number, value: unknown) => {
    setSubSimulationParams(subSimulationId, { desiredCellCount: value });
  };

  const handleRadiusChange = (subSimulationId: number, radius: unknown) => {
    setSubSimulationParams(subSimulationId, { radius });
  };

  const handleForestMinAreaChange = (value: unknown) => {
    const normalizedMinArea = typeof value === 'number' || typeof value === 'string' ? value : null;
    const nextTargets = effectiveTargets.map((target) =>
      target?.type === 'FOREST'
        ? {
            ...target,
            minArea: normalizedMinArea,
          }
        : target
    );

    setGeodataParams({
      minArea: Number(normalizedMinArea ?? FOREST_MIN_AREA_DEFAULT),
      targets: nextTargets,
    });
  };

  const addCustomSimulation = () => {
    setSimulation((prevSimulation) => {
      const previousSubSimulations = prevSimulation?.subSimulations || [];
      const nextCustomIndex =
        previousSubSimulations.filter((subSimulation) => isCustomTargetSubSimulation(subSimulation)).length + 1;
      const newSubSimulation = createCustomSubSimulation(
        getNextSubSimulationId(previousSubSimulations),
        nextCustomIndex
      );

      return {
        ...prevSimulation,
        subSimulations: [...previousSubSimulations, newSubSimulation],
      };
    });
  };

  const removeCustomSimulation = (subSimulationId: number) => {
    const targetIdsToRemove =
      customSubSimulations.find((subSimulation) => subSimulation.id === subSimulationId)?.params?.targetIds ?? [];

    setSimulation((prevSimulation) => ({
      ...prevSimulation,
      subSimulations: (prevSimulation?.subSimulations || []).filter(
        (subSimulation) => subSimulation.id !== subSimulationId
      ),
    }));
    removeCustomTargetsByIds(toNumericTargetIds(targetIdsToRemove));
  };

  const handleCustomEnabledChange = (checked: boolean) => {
    hasUserToggledCustomViewRef.current = true;
    setIsCustomChecked(checked);

    if (checked) {
      clearValidationForGeodataCustomPolygon();
    } else {
      setIsGeodataCustomPolygonValid(true);
      setGeodataCustomPolygonErrorMessage('');
    }

    if (!checked) {
      const allCustomTargetIds = effectiveTargets
        .filter((target) => target?.type === 'CUSTOM')
        .map((target) => Number(target?.id))
        .filter((targetId): targetId is number => Number.isFinite(targetId));

      setSimulation((prevSimulation) => ({
        ...prevSimulation,
        subSimulations: (prevSimulation?.subSimulations || []).filter(
          (subSimulation) => !isCustomTargetSubSimulation(subSimulation)
        ),
      }));
      removeCustomTargetsByIds(allCustomTargetIds);
      return;
    }

    if (customSubSimulations.length === 0) {
      addCustomSimulation();
    }
  };

  const setPolygonDrawButtonActive = (subSimulationId: number, isActive: boolean) => {
    const selector = `[data-custom-simulation-id="${subSimulationId}"] .border-action-group button[data-draw-source="polygon"]`;
    const button = document.querySelector(selector);
    if (!button) return;

    if (isActive) {
      button.setAttribute('data-draw-active', 'true');
      button.classList.add('draw-button-active');
    } else {
      button.removeAttribute('data-draw-active');
      button.classList.remove('draw-button-active');
    }
  };

  const cancelActivePolygonDrawing = (subSimulationId: number) => {
    const container = document.querySelector(`[data-custom-map-id="${subSimulationId}"]`);
    const actionLinks = Array.from(container?.querySelectorAll<HTMLAnchorElement>('.leaflet-draw-actions a') ?? []);
    const cancelLink = actionLinks.find((link) => {
      const title = (link.getAttribute('title') || '').toLowerCase();
      return title.includes('cancel') || title.includes('annulla');
    });

    if (cancelLink) {
      cancelLink.click();
      return;
    }

    const escEvent = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true,
    });
    document.dispatchEvent(escEvent);
  };

  const startDrawing = (subSimulationId: number) => {
    const map = customMapRefs.current[subSimulationId];
    if (map) new L.Draw.Polygon(map).enable();
  };

  const handleCreated = (subSimulationId: number) => (event: DrawCreatedEvent) => {
    setPolygonDrawButtonActive(subSimulationId, false);
    if (hasOutsideVertexBySimulationRef.current[subSimulationId]) {
      hasOutsideVertexBySimulationRef.current[subSimulationId] = false;
      event.layer.remove();
      return;
    }

    const newCoordinates = roundCoordinatesTo8Digits(event.layer._latlngs[0]);
    const formattedCoordinates = newCoordinates.map((coordinate) => [coordinate.lng, coordinate.lat]);
    formattedCoordinates.push(formattedCoordinates[0]);

    const newPolygon = turf.polygon([formattedCoordinates]);
    const selfIntersect = turf.kinks(newPolygon);
    if (selfIntersect.features.length !== 0) {
      context.notification.show('The polygon intersects itself!', 'error', true, 'toast');
      event.layer.remove();
      return;
    }

    const nextTargetId = getNextTargetId(effectiveTargets);
    const customPolygonCount = getCustomPolygonsForSubSimulation(
      customSubSimulations.find((subSimulation) => subSimulation.id === subSimulationId) ??
        ({
          id: subSimulationId,
          params: { targetIds: [] },
        } as SubSimulation)
    ).length;
    const nextTarget: GeodataTarget = {
      id: nextTargetId,
      type: 'CUSTOM',
      name: `Polygon ${customPolygonCount + 1}`,
      radius: null,
      polygon: [newCoordinates.map((coordinate) => wgs84ToLv95ForOneCoordinate(coordinate.lng, coordinate.lat))],
      isSessionCustomPolygon: true,
    };

    replaceTargets([...effectiveTargets, nextTarget]);
    setSimulation((prevSimulation) => ({
      ...prevSimulation,
      subSimulations: (prevSimulation?.subSimulations || []).map((subSimulation) => {
        if (subSimulation.id !== subSimulationId) {
          return subSimulation;
        }

        const nextTargetIds = [...toNumericTargetIds(subSimulation.params?.targetIds), nextTargetId];
        return {
          ...subSimulation,
          params: buildCustomTargetParams(subSimulation.params, nextTargetIds),
        };
      }),
    }));

    event.layer.remove();
    setIsGeodataCustomPolygonValid(true);
    setGeodataCustomPolygonErrorMessage('');
  };

  const removePolygon = (subSimulationId: number, targetIdToRemove: number) => {
    const targetSubSimulation = customSubSimulations.find((subSimulation) => subSimulation.id === subSimulationId);
    if (!targetSubSimulation) {
      return;
    }

    const nextTargetIds = toNumericTargetIds(targetSubSimulation.params?.targetIds).filter(
      (targetId) => targetId !== targetIdToRemove
    );
    setSimulation((prevSimulation) => ({
      ...prevSimulation,
      subSimulations: (prevSimulation?.subSimulations || []).map((subSimulation) =>
        subSimulation.id === subSimulationId
          ? {
              ...subSimulation,
              params: buildCustomTargetParams(subSimulation.params, nextTargetIds),
            }
          : subSimulation
      ),
    }));
    removeCustomTargetsByIds([targetIdToRemove]);
  };

  const handleDrawStart = (subSimulationId: number) => () => {
    hasOutsideVertexBySimulationRef.current[subSimulationId] = false;
    isCancellingDrawBySimulationRef.current[subSimulationId] = false;
    setPolygonDrawButtonActive(subSimulationId, true);
  };

  const handleDrawStop = (subSimulationId: number) => () => {
    isCancellingDrawBySimulationRef.current[subSimulationId] = false;
    setPolygonDrawButtonActive(subSimulationId, false);
  };

  const handleOutsideFarTerrainVertex = (subSimulationId: number) => () => {
    if (isCancellingDrawBySimulationRef.current[subSimulationId]) return;
    isCancellingDrawBySimulationRef.current[subSimulationId] = true;
    hasOutsideVertexBySimulationRef.current[subSimulationId] = true;
    context.notification.show('You should not define targets outside from the far terrain', 'error', true, 'toast');
    setTimeout(() => {
      cancelActivePolygonDrawing(subSimulationId);
    }, 0);
  };

  if (subSimulations.filter((subSimulation) => subSimulation.simType === 'TARGETVIEW').length === 0) {
    return <p style={{ margin: '0.35rem 0', color: 'rgba(0, 0, 0, 0.6)' }}>No Target View available.</p>;
  }

  const canAddAnotherSimulation =
    customSubSimulations.length === 0 ||
    getCustomPolygonsForSubSimulation(customSubSimulations[customSubSimulations.length - 1]).length > 0;

  return (
    <>
      {standardSubSimulations.map((subSimulation, index) => {
        const targetType = getTargetType(subSimulation);

        return (
          <div key={subSimulation.id}>
            <FormControlLabel
              className="checkbox-row"
              control={
                <Checkbox
                  name="enabled"
                  checked={Boolean(subSimulation.params?.enabled)}
                  disabled={readOnly}
                  onChange={(event) => handleEnabledChange(subSimulation.id, event.target.checked)}
                />
              }
              label={getTargetLabel(targetType)}
            />

            {Boolean(subSimulation.params?.enabled) && (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(240px, 1.6fr) repeat(3, minmax(140px, 1fr))',
                    columnGap: '24px',
                    rowGap: '0',
                    alignItems: 'start',
                  }}
                >
                  <TextInput
                    label="Simulation Name"
                    name="name"
                    value={subSimulation?.name || ''}
                    width={targetViewNameInputWidth}
                    disabled={readOnly}
                    onChange={(_name, value) => handleNameChange(subSimulation.id, value)}
                  />

                  <div style={{ ...targetViewSettingsStyle, marginTop: 0 }}>
                    <div style={{ marginBottom: '0.35rem' }}>Rays number</div>
                    <UMInputNumber
                      key={`${subSimulation.id}-desiredCellCount`}
                      name="desiredCellCount"
                      width={84}
                      maxErrorMessageWidth={200}
                      value={toInputValue(subSimulation?.params?.desiredCellCount) ?? DEFAULT_TARGET_VIEW_RAYS_NUMBER}
                      onChange={(_name, value) => handleDesiredCellCountChange(subSimulation.id, value)}
                      defaultValue={DEFAULT_TARGET_VIEW_RAYS_NUMBER}
                      min={MIN_TARGET_VIEW_RAYS_NUMBER}
                      max={MAX_TARGET_VIEW_RAYS_NUMBER}
                      valid={isInputNumberInRange(
                        subSimulation?.params?.desiredCellCount,
                        MIN_TARGET_VIEW_RAYS_NUMBER,
                        MAX_TARGET_VIEW_RAYS_NUMBER
                      )}
                      errorMessage={getInputNumberRangeErrorMessage(
                        MIN_TARGET_VIEW_RAYS_NUMBER,
                        MAX_TARGET_VIEW_RAYS_NUMBER
                      )}
                      maxDecimals={0}
                      disabled={readOnly}
                    />
                  </div>

                  <div style={{ ...targetViewSettingsStyle, marginTop: 0 }}>
                    <div style={{ marginBottom: '0.35rem' }}>Radius [m]</div>
                    <UMInputNumber
                      key={`${subSimulation.id}-radius`}
                      name="radius"
                      width={84}
                      maxErrorMessageWidth={200}
                      value={toInputValue(subSimulation?.params?.radius) ?? null}
                      onChange={(_name, value) => handleRadiusChange(subSimulation.id, value)}
                      defaultValue={undefined}
                      min={TARGET_VIEW_RADIUS_MIN}
                      max={targetViewRadiusMax}
                      valid={isInputNumberInRange(
                        subSimulation?.params?.radius,
                        TARGET_VIEW_RADIUS_MIN,
                        targetViewRadiusMax
                      )}
                      errorMessage={getInputNumberRangeErrorMessage(TARGET_VIEW_RADIUS_MIN, targetViewRadiusMax)}
                      maxDecimals={0}
                      disabled={readOnly}
                    />
                  </div>

                  {targetType === 'FOREST' && (
                    <div style={{ ...targetViewSettingsStyle, marginTop: 0 }}>
                      <div style={{ marginBottom: '0.35rem' }}>Min Area [m²]</div>
                      <UMInputNumber
                        key={`${subSimulation.id}-minArea`}
                        name="minArea"
                        width={84}
                        maxErrorMessageWidth={200}
                        value={toInputValue(geodataParams?.minArea) ?? FOREST_MIN_AREA_DEFAULT}
                        onChange={(_name, value) => handleForestMinAreaChange(value)}
                        defaultValue={FOREST_MIN_AREA_DEFAULT}
                        min={FOREST_MIN_AREA_MIN}
                        max={FOREST_MIN_AREA_MAX}
                        valid={isInputNumberInRange(geodataParams?.minArea, FOREST_MIN_AREA_MIN, FOREST_MIN_AREA_MAX)}
                        errorMessage={getInputNumberRangeErrorMessage(FOREST_MIN_AREA_MIN, FOREST_MIN_AREA_MAX)}
                        maxDecimals={0}
                        disabled={readOnly}
                      />
                    </div>
                  )}

                  {targetType !== 'FOREST' && <div />}
                </div>
              </div>
            )}

            {index < standardSubSimulations.length - 1 && <Divider sx={standardTargetDividerStyle} />}
          </div>
        );
      })}

      <div style={{ marginTop: '0.8rem' }}>
        {standardSubSimulations.length > 0 && <Divider sx={standardTargetDividerStyle} />}
        <FormControlLabel
          className="checkbox-row"
          control={
            <Checkbox
              checked={isCustomChecked}
              disabled={readOnly}
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleCustomEnabledChange(event.target.checked)}
            />
          }
          label="Custom Polygons"
        />

        {isCustomChecked && (
          <>
            {!readOnly && !isGeodataCustomPolygonValid && geodataCustomPolygonErrorMessage && (
              <p className="custom-error-message-analysis-area">{geodataCustomPolygonErrorMessage}</p>
            )}

            {customSubSimulations.map((subSimulation, index) => {
              const polygons = getCustomPolygonsForSubSimulation(subSimulation);

              return (
                <div
                  key={subSimulation.id}
                  data-custom-simulation-id={subSimulation.id}
                  style={{ marginTop: index === 0 ? '0.5rem' : '1.25rem' }}
                >
                  {polygons.length > 0 ? (
                    <>
                      <Grid cols="3">
                        <TextInput
                          label="Simulation Name"
                          name="name"
                          value={subSimulation?.name || ''}
                          width={targetViewNameInputWidth}
                          disabled={readOnly}
                          onChange={(_name, value) => handleNameChange(subSimulation.id, value)}
                        />

                        <div style={{ ...targetViewSettingsStyle, marginTop: 0 }}>
                          <div style={{ marginBottom: '0.35rem' }}>Rays number</div>
                          <UMInputNumber
                            key={`${subSimulation.id}-desiredCellCount`}
                            name="desiredCellCount"
                            width={84}
                            maxErrorMessageWidth={200}
                            value={
                              toInputValue(subSimulation?.params?.desiredCellCount) ?? DEFAULT_TARGET_VIEW_RAYS_NUMBER
                            }
                            onChange={(_name, value) => handleDesiredCellCountChange(subSimulation.id, value)}
                            defaultValue={DEFAULT_TARGET_VIEW_RAYS_NUMBER}
                            min={MIN_TARGET_VIEW_RAYS_NUMBER}
                            max={MAX_TARGET_VIEW_RAYS_NUMBER}
                            valid={isInputNumberInRange(
                              subSimulation?.params?.desiredCellCount,
                              MIN_TARGET_VIEW_RAYS_NUMBER,
                              MAX_TARGET_VIEW_RAYS_NUMBER
                            )}
                            errorMessage={getInputNumberRangeErrorMessage(
                              MIN_TARGET_VIEW_RAYS_NUMBER,
                              MAX_TARGET_VIEW_RAYS_NUMBER
                            )}
                            maxDecimals={0}
                            disabled={readOnly}
                          />
                        </div>

                        <div />
                      </Grid>
                    </>
                  ) : (
                    !readOnly && (
                      <p style={{ margin: '0 0 0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                        Draw at least one polygon for this simulation.
                      </p>
                    )
                  )}

                  {!readOnly && (
                    <div
                      style={{ marginBottom: '0.9rem', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <ButtonGroup variant="contained" color="error" sx={{ marginRight: '0px', boxShadow: 'none' }}>
                          <CreateBorderButton
                            color="error"
                            startDrawing={() => startDrawing(subSimulation.id)}
                            borderType="customPolygonForTarget"
                            btnText="Custom Polygon"
                            tooltipTitle=""
                            hideLabelChip
                          />
                        </ButtonGroup>

                        {polygons.map((polygon, polygonIndex) => (
                          <div key={polygon.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span>{`Polygon ${polygonIndex + 1}`}</span>
                            {polygon.isSessionCustomPolygon && (
                              <IconButton
                                size="large"
                                color="error"
                                onClick={() => removePolygon(subSimulation.id, polygon.targetId)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                        ))}
                      </div>

                      {customSubSimulations.length > 1 && (
                        <IconButton size="large" color="error" onClick={() => removeCustomSimulation(subSimulation.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </div>
                  )}

                  {mapCenter && bounds && (
                    <TargetsMapSection
                      mapId={subSimulation.id}
                      mapCenter={mapCenter}
                      mapZoom={zoomValue}
                      bounds={bounds}
                      boundaryLabel={targetViewLimitLabel}
                      customPolygons={polygons}
                      siteCenterLocation={resolvedCenterLocation}
                      coordinateSystem={coordinateSystemPreference}
                      onCreated={handleCreated(subSimulation.id)}
                      onDrawStart={handleDrawStart(subSimulation.id)}
                      onDrawStop={handleDrawStop(subSimulation.id)}
                      onOutsideFarTerrainVertex={handleOutsideFarTerrainVertex(subSimulation.id)}
                      readOnly={readOnly}
                      mapRefs={customMapRefs}
                    />
                  )}

                  {index < customSubSimulations.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                </div>
              );
            })}

            {!readOnly && (
              <UMButton
                style={{ marginTop: 6 }}
                onClick={addCustomSimulation}
                text={'Add sim'}
                disabled={!canAddAnotherSimulation}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TargetView;
